import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, ProjectDialog, ArrayHelper, TextInput } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import CustomTooltip from '../../../../components/tooltip/tooltip';
import MaterialModelHelper from '../../../../@types/model/masterData/material/materialModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import { IMaterialFormValues } from '../../../../@types/model/masterData/material/materialFormValues';
import MaterialCostForm from './MaterialCostForm';
import { IMaterialCostFormValues } from '../../../../@types/model/masterData/materialCost/materialCostFormValues';
import lodash from 'lodash';
import MaterialCostModelHelper from '../../../../@types/model/masterData/materialCost/materialCostModelHelper';
import { useAppSelector } from '../../../../@types/redux';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IMaterialFormProps {
    isLoading : boolean;
    initialValues : IMaterialFormValues;
    onSubmit : (values : IMaterialFormValues) => void;
    onClose : () => void;
}

const MaterialForm = (props : IMaterialFormProps) : React.ReactElement => {
    const palletBaseTypes = useAppSelector(x => x.masterData.palletBaseTypes);
    const materialTypes = useAppSelector(x => x.masterData.materialTypes);
    const unitOfMeasures = useAppSelector(x => x.masterData.unitOfMeasures);
    const packs = useAppSelector(x => x.masterData.packs);

    const [isMaterialCostDialogOpen, setIsMaterialCostDialogOpen] = useState<boolean>(false);

    const { handleSubmit, control, formState: { isValid, isDirty }, watch, setValue, trigger } = useForm<IMaterialFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(MaterialModelHelper.formSchema()),
        mode: 'onChange',
    });

    const currentMaterialCosts = watch('materialCosts');
    const watchedPalletBaseType = watch('palletBaseType');
    const watchedPacks = watch('packs');

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openMaterialCostDialog = () => {
        setIsMaterialCostDialogOpen(true);
    };

    const closeMaterialCostDialog = () => {
        setIsMaterialCostDialogOpen(false);
    };

    const saveNewMaterialCost = (values : IMaterialCostFormValues) => {
        const newMaterialCostList = ArrayHelper.addElement(currentMaterialCosts, values);
        setValue('materialCosts', newMaterialCostList, { shouldValidate: true, shouldDirty: true });
        closeMaterialCostDialog();
    };

    const onPalletBaseTypeChange = () : void => {
        setValue('packs', [], { shouldValidate: true, shouldDirty: true })
    };

    const onPacksChange = () : void => {
        setValue('palletBaseType', null, { shouldValidate: true, shouldDirty: true })
    };

    const getMaterialCostInitialFormValues = () : IMaterialCostFormValues => {
        return MaterialCostModelHelper.createFormValues(props.initialValues.id, null);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const palletBaseTypeOptions = useMemo(() => {
        return palletBaseTypes.filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })); // TODO: OptionType.FromDataModel
    }, [palletBaseTypes]);

    const materialTypeOptions = useMemo(() => {
        return materialTypes.filter(x => x.isActive)
            .map(x => ({ label: x.name, value: x.id })); // TODO: OptionType.FromDataModel
    }, [materialTypes]);

    const packOptions = useMemo(() => {
        return packs.filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.description}`, value: x.id })); // TODO: OptionType.FromDataModel
    }, [packs]);

    const unitOfMeasureOptions = useMemo(() => {
        return unitOfMeasures.filter(x => x.isActive)
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })); // TODO: OptionType.FromDataModel
    }, [unitOfMeasures]);

    const currentMaterialCost = useMemo<number>(() => {
        return lodash
            .chain(currentMaterialCosts)
            .filter(x => x.isActive && x.currentlyActive)
            .sumBy(x => x.amount ?? 0)
            .value();
    }, [currentMaterialCosts]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb10'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <CustomTooltip title={watchedPacks && watchedPacks.length > 0 ? 'A Pack Type is already selected.' : ''}>
                        <div className={'mb10'}>
                            <ControlledMuiAutoCompleteSelect
                                className={'p0 w100'}
                                name={'palletBaseType'}
                                label={'Pallet Base Type'}
                                control={control}
                                options={palletBaseTypeOptions}
                                defaultValues={props.initialValues.palletBaseType ?? null}
                                disabled={watchedPacks.length > 0}
                                onChange={onPalletBaseTypeChange}
                            />
                        </div>
                    </CustomTooltip>
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10'}
                        name={'materialType'}
                        label={'Material Type'}
                        control={control}
                        options={materialTypeOptions}
                        defaultValues={props.initialValues.materialType ?? null}
                    />
                    <CustomTooltip title={!!watchedPalletBaseType ? 'Pallet Base Type is already selected.' : ''}>
                        <div className={'mb10'}>
                            <ControlledMuiAutoCompleteSelect
                                className={'p0 w100'}
                                name={'packs'}
                                label={'Pack'}
                                control={control}
                                options={packOptions}
                                defaultValues={props.initialValues.packs}
                                disabled={!!watchedPalletBaseType}
                                isMulti
                                onChange={onPacksChange}
                            />
                        </div>
                    </CustomTooltip>
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10'}
                        name={'unitOfMeasure'}
                        label={'Unit Of Measure'}
                        control={control}
                        options={unitOfMeasureOptions}
                        defaultValues={props.initialValues.unitOfMeasure}
                    />
                    <div className={'fdr mb10'}>
                        <TextInput
                            label={'Current Cost (in Rand)'}
                            name={'materialCurrentCost'}
                            value={currentMaterialCost}
                            type={'number'}
                            className={'flx1'}
                            disabled={true}
                        />
                        <CustomTooltip title={'Add new material cost'}>
                            <PillButton
                                disabled={props.isLoading}
                                className={'ml10 pl20 h35 pb0 mb0'}
                                text={'Costing'}
                                color={'secondary'}
                                onClick={openMaterialCostDialog}
                            />
                        </CustomTooltip>
                    </div>
                    <div className={'fdr jcfs mb5'}>
                        <div className={'mr10'}>
                            <ControlledLabelledCheckBox
                                name={'isInnerPackaging'}
                                label={'Is Inner Packaging'}
                                control={control}
                                defaultValue={props.initialValues.isInnerPackaging}
                            />
                        </div>
                        <ControlledLabelledCheckBox
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || props.isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'}
                        />
                    </div>
                </div>
                {isMaterialCostDialogOpen &&
                    <ProjectDialog
                        title={'Add New Material Cost'}
                        isOpen={isMaterialCostDialogOpen}
                        onClose={closeMaterialCostDialog}
                        maxWidth={'sm'}
                        fullWidth
                    >
                        <MaterialCostForm
                            initialValues={getMaterialCostInitialFormValues()}
                            onSubmit={saveNewMaterialCost}
                            onClose={closeMaterialCostDialog}
                            isLoading={props.isLoading}
                        />
                    </ProjectDialog>
                }
            </form>
            <CustomReactHookFormDevTools control={control}/>
        </div>
    );
};

export default MaterialForm;
